define((require) => {
    "use strict";

    const {find}                  = require("underscore");
    const Preconditions           = require("shared/util/Preconditions");
    const Storage                 = require("shared/services/Storage");

    const SORTING_KEY = "sorting";
    const SOURCE_KEY  = "source";

    /**
     * Provides the sorting options for the image library views.
     *
     * @see ImageLibraryView
     */
    return {

        /**
         * @returns {ImageLibrarySorting}
         */
        provide({sources, storage = Storage.Local}) {
            Preconditions.checkState(Array.isArray(sources) && sources.length > 0, "Missing sources in the image library sorting provider");

            return {

                /**
                 * Return an array of the provided source items (such as creator, scene7, etc.)
                 *
                 * @return {Array} Array of source items
                 */
                getSourceItems() {
                    return sources.map((sourceItem) => sourceItem.source);
                },

                /**
                 * Verifies specified source and returns it if valid.
                 * Defaults to the first item in the sources.
                 *
                 * @return {ImageLibrarySourceEnum} ImageLibrarySourceEnum constant
                 */
                getInitialSource(source) {
                    const sourceItems = this.getSourceItems();
                    const foundSource = find(sourceItems, (sourceItem) => sourceItem === source);
                    const fallbackSource = sources[0].source;

                    if (!foundSource) {
                        this.setSelectedSource(fallbackSource);
                        return fallbackSource;
                    }

                    return foundSource;
                },

                /**
                 * Return the selected source in the localStorage
                 * Defaults to the first item in the sources.
                 *
                 * @return {ImageLibrarySourceEnum} ImageLibrarySourceEnum constant
                 */
                getSelectedSource() {
                    const storedSourceName = storage.get(Storage.Keys.IMAGE_LIBRARY_SORTING_PANEL, SOURCE_KEY);
                    const currentSource = this.lookupSourceName(storedSourceName);
                    return currentSource || sources[0].source;
                },

                /**
                 * Updates the provided storage with the name value of the given source
                 *
                 * @param  {ImageLibrarySourceEnum} source A ImageLibrarySourceEnum constant
                 */
                setSelectedSource(source) {
                    const sourceItems = this.getSourceItems();
                    Preconditions.checkState(sourceItems.indexOf(source) > -1);
                    storage.update(Storage.Keys.IMAGE_LIBRARY_SORTING_PANEL, SOURCE_KEY, source.name);
                },

                /**
                 * Returns ImageLibrarySourceEnum constant containing the given source name value
                 *
                 * @param  {String} sourceName A valid name among the constants in ImageLibrarySourceEnum
                 * @return {ImageLibrarySourceEnum} ImageLibrarySourceEnum constant if found otherwise undefined
                 */
                lookupSourceName(sourceName) {
                    const sourceItems = this.getSourceItems();
                    return find(sourceItems, (sourceItem) => sourceItem.name === sourceName);
                },

                /**
                 * Return the sorting items of a given source
                 *
                 * @param  {ImageLibrarySourceEnum} constant A valid source constant in ImageLibrarySourceEnum
                 * @return {Array} Array of sorting items in the source, if the source was found otherwise undefined
                 */
                getSortingItemsFromSource(source) {
                    const sourceMatch = find(sources, (sourceItem) => sourceItem.source === source);
                    return sourceMatch && sourceMatch.sorting;
                },

                /**
                 * Returns the sorting items in the selected source
                 *
                 * @return {Array} Array of ImageLibrarySortingEnum constants if found otherwise undefined
                 */
                getSortingItems() {
                    const source = this.getSelectedSource();
                    const sorting = this.getSortingItemsFromSource(source);
                    return sorting;
                },

                /**
                 * Returns the selected sorting item in the selected source
                 *
                 * @return {ImageLibrarySortingEnum} ImageLibrarySortingEnum constant if found, defaults to Last Uploaded
                 */
                getSelectedSorting() {
                    const storedSortingName = storage.get(Storage.Keys.IMAGE_LIBRARY_SORTING_PANEL, SORTING_KEY);
                    const currentSorting = this.lookupSortingName(storedSortingName);
                    return currentSorting || sources[0].sorting[0];
                },

                /**
                 * Set the selected sorting to a valid sorting item name
                 *
                 * @param  {ImageLibrarySortingEnum} constant A valid sorting constant in ImageLibrarySortingEnum
                 */
                setSelectedSorting(sorting) {
                    const sortingItems = this.getSortingItems();
                    Preconditions.checkState(sortingItems.indexOf(sorting) > -1);
                    storage.update(Storage.Keys.IMAGE_LIBRARY_SORTING_PANEL, SORTING_KEY, sorting.name);
                },

                /**
                 * Return the sorting based on a sorting name
                 *
                 * @param  {String} name A valid sorting name value in a ImageLibrarySortingEnum constant
                 * @param  {ImageLibrarySourceEnum} source A provided source from ImageLibrarySortingEnum
                 * @return {ImageLibrarySortingEnum} constant if the source found otherwise undefined
                 */
                lookupSortingName(name, source) {
                    let sorting;

                    if (source) {
                        const sourceMatch = find(sources, (sourceSortingItem) => sourceSortingItem.source === source);
                        sorting = sourceMatch ? sourceMatch.sorting : [];
                    } else {
                        sorting = this.getSortingItems();
                    }

                    return find(sorting, (sortingItem) => sortingItem.name === name);
                },

                /**
                 * Return the sorting based on a sorting name
                 *
                 * @return {ImageLibrarySortingEnum} constant The first sorting item in the selected source
                 */
                getDefaultSorting() {
                    const sortingItems = this.getSortingItems();
                    return sortingItems && sortingItems[0];
                }
            };
        },

        SORTING_KEY,
        SOURCE_KEY
    };
});



// WEBPACK FOOTER //
// ./src/editor/ui/imageLibrary/ImageLibrarySortingProvider.js